import { Injectable, inject } from '@angular/core';

import { EMPTY } from 'rxjs';
import { mergeMap, withLatestFrom } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { CancelledDeviceTaskService, DeviceTaskService } from '@era/device-task-management-sdk';
import { DevicesRouterStoreSelectors, safeMap } from '@era/stores';
import { Store } from '@ngrx/store';
import {
  DeviceScheduledTasksApiActions,
  DeviceScheduledTasksResolverActions,
  DeviceScheduledTasksUiActions,
  SignalRActions,
} from './device-scheduled-tasks.actions';

@Injectable()
export class DeviceScheduledTasksEffects {
  private readonly actions = inject(Actions);
  private readonly deviceTaskService = inject(DeviceTaskService);
  private readonly cancelledDeviceTaskService = inject(CancelledDeviceTaskService);
  private readonly store = inject(Store);

  scheduledTasksRequestedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeviceScheduledTasksResolverActions.scheduledTasksRequested),
      mergeMap(({ udid }) =>
        safeMap(
          this.deviceTaskService.getDeviceTasks({ udid }),
          (scheduledTasks) => DeviceScheduledTasksApiActions.scheduledTasksLoaded({ scheduledTasks }),
          (error) =>
            DeviceScheduledTasksApiActions.scheduledTasksLoadedError({
              error,
            })
        )
      )
    );
  });

  scheduledTaskDeletedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeviceScheduledTasksUiActions.scheduledTaskDeleted),
      mergeMap(({ deviceTask }) =>
        safeMap(
          this.cancelledDeviceTaskService.cancel({
            id: deviceTask.messageId ?? undefined,
            udid: deviceTask.udid ?? undefined,
            messageText: deviceTask.task ?? undefined,
          }),
          () => DeviceScheduledTasksApiActions.scheduledTaskDeleted({ deviceTask }),
          (error) =>
            DeviceScheduledTasksApiActions.scheduledTaskDeletedError({
              error,
            })
        )
      )
    );
  });

  scheduledTasksDeletedEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeviceScheduledTasksUiActions.scheduledTasksDeleted),
      withLatestFrom(this.store.select(DevicesRouterStoreSelectors.selectUdid)),
      mergeMap(([, udid]) =>
        udid
          ? safeMap(
              this.deviceTaskService.cancelDeviceTasks({ udid }),
              () => DeviceScheduledTasksApiActions.scheduledTasksDeleted({ udid }),
              (error) =>
                DeviceScheduledTasksApiActions.scheduledTasksDeletedError({
                  error,
                })
            )
          : EMPTY
      )
    );
  });

  scheduleTaskEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(DeviceScheduledTasksUiActions.taskScheduled),
      withLatestFrom(this.store.select(DevicesRouterStoreSelectors.selectUdid)),
      mergeMap(([{ taskType }, udid]) =>
        udid
          ? safeMap(
              this.deviceTaskService.startSync({ udid, type: taskType }),
              () => DeviceScheduledTasksApiActions.taskScheduled({ udid, taskType }),
              (error) =>
                DeviceScheduledTasksApiActions.taskScheduledError({
                  error,
                })
            )
          : EMPTY
      )
    );
  });

  signalRScheduledTasksUpdateEffect$ = createEffect(() => {
    return this.actions.pipe(
      ofType(SignalRActions.scheduledTasksUpdated),
      mergeMap(({ udid }) =>
        safeMap(
          this.deviceTaskService.getDeviceTasks({ udid }),
          (scheduledTasks) => DeviceScheduledTasksApiActions.scheduledTasksLoaded({ scheduledTasks }),
          (error) =>
            DeviceScheduledTasksApiActions.scheduledTasksLoadedError({
              error,
            })
        )
      )
    );
  });
}
